'use client';
import React from 'react';
import { api } from '@/trpc/react';
import { match } from 'ts-pattern';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Control, useController } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { isOptionalWildcard, formatText } from '@acme/lib';
import { WildcardCard } from '@/components/Cards/WildcardCard';
import { CardTitle } from '@components/ui/card';
import { Card } from '@components/ui/card';
import { Check } from 'lucide-react';
import { cn } from '@lib/utils';

const CATEGORIES_WITH_CARDS = ['clothing', 'location', 'upperClothing', 'jacket', 'shirt'];

interface WildcardStepProps {
  themeId: string;
  gender?: 'male' | 'female';
  wildcardModifierKey: string;
  category: string;
  control: Control<any>;
  setValue: any;
  onColorSelect?: (selectedItem: string) => void;
}

export function WildcardItemSelectorStep({
  gender,
  wildcardModifierKey,
  category,
  control,
  setValue,
  onColorSelect
}: WildcardStepProps) {
  const modifierValuesQuery = api.theme.getModifierValuesByTagName.useQuery(
    {
      tagName: wildcardModifierKey,
      gender: gender ? [gender] : undefined,
      category
    },
    { enabled: true }
  );

  const { field } = useController({
    name: 'selectedModifiers',
    control
  });

  // Get the current value from the nested structure
  const currentValue = React.useMemo(() => {
    const categoryData = field.value?.[category]?.[wildcardModifierKey];
    return categoryData?.value || (isOptionalWildcard(category) ? 'None' : undefined);
  }, [field.value, category, wildcardModifierKey]);

  const handleSelectionChange = (value: string) => {
    const currentModifiers = { ...field.value };

    if (value === 'Random' || value === 'None') {
      // For Random/None, clear the entire category and set just the value
      currentModifiers[category] = {
        [wildcardModifierKey]: { value }
      };
    } else {
      // For specific selections, clear the entire category and set the new value
      const existingModifierData = currentModifiers[category]?.[wildcardModifierKey] || {};
      currentModifiers[category] = {
        [wildcardModifierKey]: {
          // Spread existing properties (like color) but exclude the old value
          ...existingModifierData,
          value // Set the new value
        }
      };
    }

    // Update the form state
    setValue('selectedModifiers', currentModifiers);

    // Check if the selected value includes "{color}" and it's not Random or None
    if (value !== 'Random' && value !== 'None' && value.includes('{color}') && onColorSelect) {
      onColorSelect(value);
    }
  };

  // Store the random sample URLs in state to keep them stable
  const [randomSampleUrls, setRandomSampleUrls] = React.useState<string[]>([]);

  // Function to get 4 random sample URLs
  const getRandomSampleUrls = React.useCallback((options: any[]) => {
    const allSampleUrls = options.flatMap(option => option.sampleImageUrls || []);
    const shuffled = [...allSampleUrls].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  }, []);

  // Function to get the first 4 sample URLs from an option
  const getSampleUrls = React.useCallback((sampleImageUrls: string[] = []) => {
    return sampleImageUrls.slice(0, 4);
  }, []);

  // Update random samples only when options change
  React.useEffect(() => {
    if (modifierValuesQuery.data) {
      setRandomSampleUrls(getRandomSampleUrls(modifierValuesQuery.data));
    }
  }, [modifierValuesQuery.data, getRandomSampleUrls]);

  return (
    <div className="flex flex-col h-full">
      {match(modifierValuesQuery)
        .with({ status: 'pending' }, () => (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ))
        .with({ status: 'error' }, () => <div>Error loading options</div>)
        .with({ status: 'success' }, ({ data: options }) => {
          // Filter out options with null values
          const validOptions = options.filter(
            (option): option is typeof option & { value: string } => option.value !== null
          );

          return CATEGORIES_WITH_CARDS.includes(category) ? (
            <ScrollArea className="flex-grow -px-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-2">
                {isOptionalWildcard(category) && (
                  <Card
                    key="none"
                    className={cn(
                      'p-3 min-w-0 relative cursor-pointer',
                      currentValue === 'None' ? 'ring-2 ring-muted-foreground/10' : ''
                    )}
                    onClick={() => handleSelectionChange('None')}
                  >
                    <div className="relative grid grid-cols-2 flex-1 gap-[1px] auto-rows-fr aspect-square mb-2.5">
                      {currentValue === 'None' && (
                        <div className="absolute z-[1] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-3 border-2 border-white shadow-md">
                          <Check size={16} />
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-sm text-center line-clamp-2">None</CardTitle>
                  </Card>
                )}
                <WildcardCard
                  key="random"
                  title="Random"
                  checked={currentValue === 'Random'}
                  onCheckedChange={() => handleSelectionChange('Random')}
                  imageUrls={randomSampleUrls}
                />
                {validOptions.map((option, index) => (
                  <WildcardCard
                    key={`${option.value}-${index}`}
                    title={formatText(option.value.replace(/\{.*?\}/g, ''), 'title')}
                    checked={currentValue === option.value}
                    onCheckedChange={() => handleSelectionChange(option.value)}
                    imageUrls={getSampleUrls(option.sampleImageUrls)}
                  />
                ))}
              </div>
            </ScrollArea>
          ) : (
            <RadioGroup
              value={currentValue}
              onValueChange={handleSelectionChange}
              className="divide-y-[1px] divide-border flex flex-col gap-0"
            >
              {isOptionalWildcard(category) && (
                <div className="flex items-center space-x-2 py-2 hover:bg-muted hover:cursor-pointer">
                  <RadioGroupItem value="None" id="wildcard-none" variant="check" />
                  <Label htmlFor="wildcard-none" className="text-base">
                    None
                  </Label>
                </div>
              )}
              <div className="flex items-center space-x-2 py-2 hover:bg-muted hover:cursor-pointer">
                <RadioGroupItem value="Random" id="wildcard-random" variant="check" />
                <Label htmlFor="wildcard-random" className="text-base">
                  Random
                </Label>
              </div>
              {validOptions.map((option, index) => (
                <div
                  key={`${option.value}-${index}`}
                  className="flex items-center space-x-2 py-2 hover:bg-muted hover:cursor-pointer"
                >
                  <RadioGroupItem value={option.value} id={`wildcard-${index}`} variant="check" />
                  <Label htmlFor={`wildcard-${index}`} className="text-base">
                    {formatText(option.value, 'title')}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          );
        })
        .exhaustive()}
    </div>
  );
}
